import { useVerifyTwoFactor } from '@/hooks/useAuth';
import { TwoFactorVerifyData } from '@/types/auth.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Shield } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const verificationSchema = z.object({
  token: z
    .string()
    .length(6, 'auth.validation.twoFactor.tokenLength')
    .regex(/^\d+$/, 'auth.validation.twoFactor.tokenDigits'),
});

interface TwoFactorVerificationFormProps {
  tempToken: string;
  onBack?: () => void;
  /**
   * [AUTO-INIT VAULT AU LOGIN 2026-07] Voir OtpVerificationForm.
   */
  loginPassword?: string;
}

export const TwoFactorVerificationForm = ({
  tempToken,
  onBack,
  loginPassword,
}: TwoFactorVerificationFormProps) => {
  const { t } = useTranslation();
  const verifyTwoFactor = useVerifyTwoFactor(loginPassword);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<{ token: string }>({
    resolver: zodResolver(verificationSchema),
  });

  const onSubmit = async (data: { token: string }) => {
    try {
      const verifyData: TwoFactorVerifyData = {
        token: data.token,
        tempToken,
      };
      await verifyTwoFactor.mutateAsync(verifyData);
    } catch {
      // Error is handled by the hook with toast
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
          <Shield className="h-8 w-8 text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-neutral-900">
          {t('auth.twoFactor.verificationTitle')}
        </h2>
        <p className="mt-2 text-sm text-neutral-600">
          {t('auth.twoFactor.verificationDescription')}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label
            htmlFor="token"
            className="block text-sm font-medium text-neutral-700"
          >
            {t('auth.twoFactor.tokenLabel')}
          </label>
          <div className="relative mt-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <KeyRound className="h-5 w-5 text-neutral-400" />
            </div>
            <input
              {...register('token')}
              type="text"
              id="token"
              maxLength={6}
              placeholder="000000"
              className="block w-full rounded-xl border border-neutral-300 py-2 pl-10 pr-3 text-center text-2xl font-mono tracking-wider shadow-sm transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="\d{6}"
            />
          </div>
          {errors.token && (
            <p className="mt-1 text-sm text-error-600">
              {t(errors.token.message as string)}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex w-full justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
          >
            {isSubmitting
              ? t('common.verifying')
              : t('auth.twoFactor.verify')}
          </button>
        </div>

        {/* Back Button */}
        {onBack && (
          <div className="text-center">
            <button
              type="button"
              onClick={onBack}
              className="text-sm font-medium text-brand-600 transition-colors hover:text-brand-500"
            >
              {t('auth.twoFactor.backToLogin')}
            </button>
          </div>
        )}
      </form>

      {/* Help Text */}
      <div className="rounded-lg border border-info-200 bg-info-50 p-3">
        <p className="text-xs text-info-700">
          {t('auth.twoFactor.helpText')}
        </p>
      </div>
    </div>
  );
};
